<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useWhatsNew } from '../../composables/useWhatsNew'
import { registerGlobalSurfaceMessages } from '../../i18n'

import WhatsNewPanel from './WhatsNewPanel.vue'

import { useI18n, useLocaleStore } from '@/shared/i18n'
import { Icon, Skeleton } from '@/shared/ui'

const MESSAGE_KEY = 'globalSurface.whatsNew.button'
const localeStore = useLocaleStore()
const { t, te } = useI18n()
const messagesReady = ref(te(MESSAGE_KEY))
const whatsNew = useWhatsNew()

watch(
  () => localeStore.locale,
  async (locale) => {
    messagesReady.value = te(MESSAGE_KEY)
    if (messagesReady.value) return
    try {
      await registerGlobalSurfaceMessages(locale)
      messagesReady.value = true
    } catch {
      messagesReady.value = false
    }
  },
  { immediate: true },
)

// The store already surfaces a failed load through reactive `state`/`updatesError`, so this
// mount-time prefetch only needs to keep its rejection from escaping as an unhandled promise
// rejection — never a silent catch of something the UI still needs to react to.
onMounted(() => void whatsNew.load().catch(() => {}))
</script>

<template>
  <Skeleton
    v-if="!messagesReady"
    data-testid="whats-new-loading"
    width="2.5rem"
    height="2.5rem"
    radius="var(--ui-radius-lg)"
    class="bg-accent-soft"
  />
  <template v-else>
    <span
      class="whats-new-button__slot me-xs inline-flex items-center border-e border-border pe-xs"
    >
      <button
        type="button"
        class="whats-new-button relative grid h-10 w-10 flex-none cursor-pointer place-items-center rounded-lg border-0 bg-surface p-0 text-muted outline-none transition-[background-color,color,box-shadow] duration-150 hover:bg-accent-soft hover:text-highlight focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]"
        :class="{
          'whats-new-button--active !bg-accent-soft !text-highlight':
            whatsNew.isOpen.value || whatsNew.hasUnseen.value,
        }"
        :aria-label="t(MESSAGE_KEY)"
        :aria-expanded="whatsNew.isOpen.value"
        @click="whatsNew.open"
      >
        <Icon name="megaphone" weight="light" :size="18" aria-hidden="true" />
        <span
          v-if="whatsNew.hasUnseen.value"
          class="whats-new-button__dot absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-surface bg-highlight shadow-input"
          aria-hidden="true"
        />
      </button>
    </span>
    <WhatsNewPanel
      v-model="whatsNew.isOpen.value"
      :updates="whatsNew.updates.value"
      :state="whatsNew.state.value"
      :roadmap="whatsNew.roadmap.value"
      :my-votes="whatsNew.myVotes.value"
      @retry="whatsNew.retry"
      @vote="whatsNew.vote"
    />
  </template>
</template>
